import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';
import { operationsApi, type OvertimeRule, type ShiftDefinition } from '../api';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
// JS weekday() in the backend is Mon=0..Sun=6.
const DEFAULT_WORK_DAYS = [0, 1, 2, 3, 4];

export function AttendanceSettingsPage() {
  const [shifts, setShifts] = useState<ShiftDefinition[]>([]);
  const [rules, setRules] = useState<OvertimeRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, r] = await Promise.all([
        operationsApi.listShifts(),
        operationsApi.listOvertimeRules(),
      ]);
      setShifts(s);
      setRules(r);
    } catch (e) {
      setError((e as Error).message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-4xl space-y-8 px-6 py-8">
        <header className="space-y-1">
          <div className="text-xs text-muted-foreground">
            <Link to="/settings" className="hover:underline">
              Settings
            </Link>{' '}
            / Attendance
          </div>
          <h1 className="page-title flex items-center gap-2.5">
            <ClipboardList className="h-6 w-6 text-primary" />
            Attendance settings
          </h1>
          <p className="page-subtitle">
            Define shifts (lateness is measured against them) and overtime rules (drive the payroll{' '}
            <span className="font-mono">overtime_hours</span> variable).
          </p>
        </header>

        {error && (
          <div className="rounded-md border border-danger/40 bg-danger/10 px-4 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        <ShiftsSection shifts={shifts} loading={loading} onChanged={refresh} />
        <OvertimeSection rules={rules} loading={loading} onChanged={refresh} />
      </div>
    </div>
  );
}

// ── Shifts ─────────────────────────────────────────────────────────────────
function ShiftsSection({
  shifts,
  loading,
  onChanged,
}: {
  shifts: ShiftDefinition[];
  loading: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <section className="card space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Shifts</h2>
          <p className="text-sm text-muted-foreground">
            {shifts.length} shift{shifts.length === 1 ? '' : 's'} defined.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)}>
          + New shift
        </button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-2 pr-4">Name</th>
            <th className="py-2 pr-4">Slug</th>
            <th className="py-2 pr-4">Window</th>
            <th className="py-2 pr-4">Std hrs</th>
            <th className="py-2 pr-4">Days</th>
            <th className="py-2 pr-4">Enabled</th>
          </tr>
        </thead>
        <tbody>
          {shifts.map((s) => (
            <tr key={s.id} className="border-b border-border/60">
              <td className="py-2 pr-4">{s.name}</td>
              <td className="py-2 pr-4 font-mono text-xs text-muted-foreground">{s.slug}</td>
              <td className="py-2 pr-4 tabular-nums">
                {s.start_time}–{s.end_time}
              </td>
              <td className="py-2 pr-4 tabular-nums">{s.standard_hours}</td>
              <td className="py-2 pr-4 text-xs">
                {s.days_of_week.map((d) => DAY_LABELS[d]).join(', ')}
              </td>
              <td className="py-2 pr-4">{s.is_enabled ? '✓' : '—'}</td>
            </tr>
          ))}
          {!loading && shifts.length === 0 && (
            <tr>
              <td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">
                No shifts yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {open && (
        <NewShiftModal
          onClose={() => setOpen(false)}
          onCreated={() => {
            setOpen(false);
            onChanged();
          }}
        />
      )}
    </section>
  );
}

function NewShiftModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [startTime, setStartTime] = useState('06:00');
  const [endTime, setEndTime] = useState('14:00');
  const [standardHours, setStandardHours] = useState('8');
  const [days, setDays] = useState<number[]>(DEFAULT_WORK_DAYS);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggleDay = (d: number) =>
    setDays((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b),
    );

  const save = async () => {
    const cleanSlug = slug.trim().toLowerCase();
    if (!name.trim() || !cleanSlug) {
      setErr('Name and slug are required.');
      return;
    }
    if (!/^[a-z0-9_-]+$/.test(cleanSlug)) {
      setErr('Slug may only contain lowercase letters, digits, _ and -.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await operationsApi.createShift({
        slug: cleanSlug,
        name: name.trim(),
        start_time: startTime,
        end_time: endTime,
        standard_hours: Number(standardHours) || 8,
        days_of_week: days,
      });
      onCreated();
    } catch (e) {
      setErr((e as Error).message || 'Failed to create shift');
      setBusy(false);
    }
  };

  return (
    <ModalShell title="New shift" onClose={onClose}>
      <Labeled label="Name">
        <input
          className="input w-full"
          placeholder="Morning 06–14"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (!slug)
              setSlug(
                e.target.value
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/^-|-$/g, ''),
              );
          }}
          autoFocus
        />
      </Labeled>
      <Labeled label="Slug">
        <input
          className="input w-full font-mono"
          placeholder="morning-06-14"
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
        />
      </Labeled>
      <div className="grid grid-cols-3 gap-3">
        <Labeled label="Start">
          <input
            type="time"
            className="input"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
          />
        </Labeled>
        <Labeled label="End">
          <input
            type="time"
            className="input"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
          />
        </Labeled>
        <Labeled label="Std hours">
          <input
            type="number"
            step="0.5"
            className="input"
            value={standardHours}
            onChange={(e) => setStandardHours(e.target.value)}
          />
        </Labeled>
      </div>
      <div className="space-y-1">
        <span className="text-xs uppercase text-muted-foreground">Days</span>
        <div className="flex flex-wrap gap-1">
          {DAY_LABELS.map((label, idx) => (
            <button
              key={label}
              type="button"
              onClick={() => toggleDay(idx)}
              className={
                'rounded px-2 py-1 text-xs ' +
                (days.includes(idx)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground')
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {err && <p className="text-xs text-rose-300">{err}</p>}
      <ModalActions onClose={onClose} onSave={save} busy={busy} saveLabel="Create shift" />
    </ModalShell>
  );
}

// ── Overtime rules ───────────────────────────────────────────────────────
function OvertimeSection({
  rules,
  loading,
  onChanged,
}: {
  rules: OvertimeRule[];
  loading: boolean;
  onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <section className="card space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">Overtime rules</h2>
          <p className="text-sm text-muted-foreground">
            {rules.length} rule{rules.length === 1 ? '' : 's'}. Highest priority match wins.
          </p>
        </div>
        <button className="btn-primary" onClick={() => setOpen(true)}>
          + New rule
        </button>
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr className="border-b border-border">
            <th className="py-2 pr-4">Name</th>
            <th className="py-2 pr-4">Priority</th>
            <th className="py-2 pr-4">Daily &gt;</th>
            <th className="py-2 pr-4">Weekly &gt;</th>
            <th className="py-2 pr-4">Multiplier</th>
            <th className="py-2 pr-4">Days</th>
            <th className="py-2 pr-4">Enabled</th>
          </tr>
        </thead>
        <tbody>
          {rules.map((r) => (
            <tr key={r.id} className="border-b border-border/60">
              <td className="py-2 pr-4">{r.name}</td>
              <td className="py-2 pr-4 tabular-nums">{r.priority}</td>
              <td className="py-2 pr-4 tabular-nums">{r.daily_threshold_hours ?? '—'}</td>
              <td className="py-2 pr-4 tabular-nums">{r.weekly_threshold_hours ?? '—'}</td>
              <td className="py-2 pr-4 tabular-nums">×{r.multiplier}</td>
              <td className="py-2 pr-4 text-xs">
                {r.applies_on_days.length
                  ? r.applies_on_days.map((d) => DAY_LABELS[d]).join(', ')
                  : 'all'}
              </td>
              <td className="py-2 pr-4">{r.is_enabled ? '✓' : '—'}</td>
            </tr>
          ))}
          {!loading && rules.length === 0 && (
            <tr>
              <td colSpan={7} className="py-6 text-center text-sm text-muted-foreground">
                No overtime rules yet.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {open && (
        <NewOvertimeModal
          onClose={() => setOpen(false)}
          onCreated={() => {
            setOpen(false);
            onChanged();
          }}
        />
      )}
    </section>
  );
}

function NewOvertimeModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [priority, setPriority] = useState('100');
  const [dailyThreshold, setDailyThreshold] = useState('8');
  const [multiplier, setMultiplier] = useState('1.5');
  const [days, setDays] = useState<number[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggleDay = (d: number) =>
    setDays((prev) =>
      prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d].sort((a, b) => a - b),
    );

  const save = async () => {
    if (!name.trim()) {
      setErr('A name is required.');
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await operationsApi.createOvertimeRule({
        name: name.trim(),
        priority: Number(priority) || 100,
        daily_threshold_hours: dailyThreshold ? Number(dailyThreshold) : null,
        multiplier: Number(multiplier) || 1.5,
        applies_on_days: days,
      });
      onCreated();
    } catch (e) {
      setErr((e as Error).message || 'Failed to create rule');
      setBusy(false);
    }
  };

  return (
    <ModalShell title="New overtime rule" onClose={onClose}>
      <Labeled label="Name">
        <input
          className="input w-full"
          placeholder="Daily over 8h"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
      </Labeled>
      <div className="grid grid-cols-3 gap-3">
        <Labeled label="Priority">
          <input
            type="number"
            className="input"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          />
        </Labeled>
        <Labeled label="Daily threshold (h)">
          <input
            type="number"
            step="0.5"
            className="input"
            value={dailyThreshold}
            onChange={(e) => setDailyThreshold(e.target.value)}
          />
        </Labeled>
        <Labeled label="Multiplier">
          <input
            type="number"
            step="0.1"
            className="input"
            value={multiplier}
            onChange={(e) => setMultiplier(e.target.value)}
          />
        </Labeled>
      </div>
      <div className="space-y-1">
        <span className="text-xs uppercase text-muted-foreground">
          Applies on (empty = every day)
        </span>
        <div className="flex flex-wrap gap-1">
          {DAY_LABELS.map((label, idx) => (
            <button
              key={label}
              type="button"
              onClick={() => toggleDay(idx)}
              className={
                'rounded px-2 py-1 text-xs ' +
                (days.includes(idx)
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground')
              }
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      {err && <p className="text-xs text-rose-300">{err}</p>}
      <ModalActions onClose={onClose} onSave={save} busy={busy} saveLabel="Create rule" />
    </ModalShell>
  );
}

// ── Shared primitives ──────────────────────────────────────────────────────
function ModalShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-30 grid place-items-center bg-black/50 px-4">
      <div className="card w-full max-w-md space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button className="text-muted-foreground hover:text-foreground" onClick={onClose}>
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function Labeled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs uppercase text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}

function ModalActions({
  onClose,
  onSave,
  busy,
  saveLabel,
}: {
  onClose: () => void;
  onSave: () => void;
  busy: boolean;
  saveLabel: string;
}) {
  return (
    <div className="flex justify-end gap-2 pt-2">
      <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
        Cancel
      </button>
      <button type="button" className="btn-primary" onClick={onSave} disabled={busy}>
        {busy ? 'Saving…' : saveLabel}
      </button>
    </div>
  );
}
